import { useEffect, useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { BarChart3, Crown, Sparkles, TrendingUp, ArrowDownUp, Search as SearchIcon, AlertCircle, ChevronDown, SlidersHorizontal } from 'lucide-react';
import { clsx } from 'clsx';
import { useSubscriptionStore } from '../../store/subscriptionStore';
import { isApiError, getErrorMessage } from '../../services/api';
import {
  compareOperations,
  type AnalyticsCompareResult,
  type AnalyticsFiltersPayload,
} from '../../services/analytics';
import SearchFilters, {
  countActiveFilters,
  type SearchFiltersState,
  type SearchFieldErrors,
} from './SearchFilters';
import type { CategoryNode } from './CategoryMultiSelect';
import type { BankAccount, SharedBankAccount } from '../../types';
import PeriodComparisonPicker, {
  type PeriodInput,
  type PeriodFieldError,
} from './PeriodComparisonPicker';
import ComparisonTable from './ComparisonTable';
import ComparisonBarChart from './ComparisonBarChart';
import TopVariationsCard from './TopVariationsCard';

interface AnalyticsTabProps {
  filters: SearchFiltersState;
  onFiltersChange: (filters: SearchFiltersState) => void;
  fieldErrors: SearchFieldErrors;
  setFieldErrors: (errors: SearchFieldErrors) => void;
  validate: (state: SearchFiltersState) => SearchFieldErrors;
  hasCoupleAccount: boolean;
  personalAccounts: BankAccount[];
  sharedAccounts: SharedBankAccount[];
  expenseCategories: CategoryNode[];
  incomeCategories: CategoryNode[];
  partner1Name: string;
  partner2Name: string;
}

function toIsoDate(d: Date): string {
  const yyyy = d.getFullYear();
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const dd = String(d.getDate()).padStart(2, '0');
  return `${yyyy}-${mm}-${dd}`;
}

function defaultPeriods(t: (k: string, opts?: Record<string, unknown>) => string): PeriodInput[] {
  const now = new Date();
  const startCurrent = new Date(now.getFullYear(), now.getMonth(), 1);
  const endCurrent = new Date(now.getFullYear(), now.getMonth() + 1, 0);
  const startPrevious = new Date(now.getFullYear(), now.getMonth() - 1, 1);
  const endPrevious = new Date(now.getFullYear(), now.getMonth(), 0);
  return [
    { start: toIsoDate(startPrevious), end: toIsoDate(endPrevious), label: t('analytics.presetLabels.previousMonth') },
    { start: toIsoDate(startCurrent), end: toIsoDate(endCurrent), label: t('analytics.presetLabels.currentMonth') },
  ];
}

function buildAnalyticsFilters(state: SearchFiltersState, hasCoupleAccount: boolean): AnalyticsFiltersPayload {
  const scopes: ('personal' | 'shared')[] = ['personal'];
  if (hasCoupleAccount) scopes.push('shared');
  return {
    scopes,
    personalAccountIds: state.selectedPersonalIds.length ? state.selectedPersonalIds : undefined,
    sharedAccountIds: state.selectedSharedIds.length ? state.selectedSharedIds : undefined,
    expenseCategoryIds: state.categories.expenseCategoryIds.length ? state.categories.expenseCategoryIds : undefined,
    expenseSubcategoryIds: state.categories.expenseSubcategoryIds.length ? state.categories.expenseSubcategoryIds : undefined,
    incomeCategoryIds: state.categories.incomeCategoryIds.length ? state.categories.incomeCategoryIds : undefined,
    incomeSubcategoryIds: state.categories.incomeSubcategoryIds.length ? state.categories.incomeSubcategoryIds : undefined,
    operationType: state.operationType === 'all' ? undefined : state.operationType,
    amountMin: state.amountMin ? Number(state.amountMin) : undefined,
    amountMax: state.amountMax ? Number(state.amountMax) : undefined,
    recurring: state.recurring === 'all' ? undefined : state.recurring,
    recurrenceFrequencies: state.recurrenceFrequencies.length ? state.recurrenceFrequencies : undefined,
    validated: state.validated === 'all' ? undefined : state.validated,
    payerPartnerId: state.payerPartnerId === 'all' ? undefined : state.payerPartnerId,
    searchText: state.searchText || undefined,
  };
}

export default function AnalyticsTab({
  filters,
  onFiltersChange,
  fieldErrors,
  setFieldErrors,
  validate,
  hasCoupleAccount,
  personalAccounts,
  sharedAccounts,
  expenseCategories,
  incomeCategories,
  partner1Name,
  partner2Name,
}: AnalyticsTabProps) {
  const { t } = useTranslation('search');
  const navigate = useNavigate();
  const isPremiumOrAbove = useSubscriptionStore((s) => s.isPremiumOrAbove());

  const [periods, setPeriods] = useState<PeriodInput[]>(() => defaultPeriods(t));
  const [result, setResult] = useState<AnalyticsCompareResult | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [periodErrors, setPeriodErrors] = useState<PeriodFieldError[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(false);

  const activeFiltersCount = countActiveFilters(filters);

  // Reset les libellés par défaut si la langue change
  useEffect(() => {
    setPeriods((prev) => {
      // ne touche pas aux labels personnalisés (heuristique : si label match clé i18n par défaut, on remplace)
      return prev;
    });
  }, [t]);

  const features = [
    { icon: BarChart3, key: 'comparison' },
    { icon: TrendingUp, key: 'topVariations' },
    { icon: ArrowDownUp, key: 'periodVs' },
  ];

  const handleApplyPreset = useCallback(
    (preset: 'currentVsPrevious' | 'yearOverYear') => {
      const now = new Date();
      if (preset === 'currentVsPrevious') {
        setPeriods(defaultPeriods(t));
        setPeriodErrors([]);
        return;
      }
      // yearOverYear : mois en cours année courante vs même mois année précédente
      const startCurrent = new Date(now.getFullYear(), now.getMonth(), 1);
      const endCurrent = new Date(now.getFullYear(), now.getMonth() + 1, 0);
      const startLastYear = new Date(now.getFullYear() - 1, now.getMonth(), 1);
      const endLastYear = new Date(now.getFullYear() - 1, now.getMonth() + 1, 0);
      setPeriods([
        { start: toIsoDate(startLastYear), end: toIsoDate(endLastYear), label: String(now.getFullYear() - 1) },
        { start: toIsoDate(startCurrent), end: toIsoDate(endCurrent), label: String(now.getFullYear()) },
      ]);
      setPeriodErrors([]);
    },
    [t],
  );

  const validatePeriods = useCallback(
    (input: PeriodInput[]): PeriodFieldError[] => {
      const isoDateRe = /^\d{4}-\d{2}-\d{2}$/;
      return input.map((p) => {
        const e: PeriodFieldError = {};
        if (!p.label || p.label.trim().length === 0) {
          e.label = t('errors.missingPeriodLabel');
        }
        const startMissing = !p.start;
        const endMissing = !p.end;
        if (startMissing || endMissing) {
          if (startMissing) e.start = t('errors.missingPeriodDates');
          if (endMissing) e.end = t('errors.missingPeriodDates');
        } else {
          if (!isoDateRe.test(p.start)) e.start = t('errors.invalidDate');
          if (!isoDateRe.test(p.end)) e.end = t('errors.invalidDate');
          if (!e.start && !e.end && new Date(p.start) > new Date(p.end)) {
            e.start = t('errors.periodStartAfterEnd');
          }
        }
        return e;
      });
    },
    [t],
  );

  const handlePeriodsChange = (next: PeriodInput[]) => {
    setPeriods(next);
    if (periodErrors.length === 0) return;
    setPeriodErrors((prev) =>
      next.map((p, idx) => {
        const old = periods[idx];
        const cur = prev[idx] ?? {};
        if (!old) return {};
        const cleared: PeriodFieldError = { ...cur };
        if (p.label !== old.label) delete cleared.label;
        if (p.start !== old.start) {
          delete cleared.start;
          delete cleared.end;
        }
        if (p.end !== old.end) {
          delete cleared.start;
          delete cleared.end;
        }
        return cleared;
      }),
    );
  };

  const handleSubmit = async () => {
    const errs = validatePeriods(periods);
    const filterErrs = validate(filters);
    const hasPeriodErr = errs.some((e) => e.label || e.start || e.end);
    const hasFilterErr = Object.keys(filterErrs).length > 0;
    if (hasPeriodErr || hasFilterErr) {
      setPeriodErrors(errs);
      if (hasFilterErr) {
        setFieldErrors(filterErrs);
        setFiltersOpen(true);
      }
      setError(t('errors.validation'));
      return;
    }
    setPeriodErrors([]);
    setFieldErrors({});
    setIsLoading(true);
    setError(null);
    try {
      const data = await compareOperations(buildAnalyticsFilters(filters, hasCoupleAccount), periods);
      setResult(data);
    } catch (err) {
      if (isApiError(err) && err.response?.status === 400) {
        const data = err.response.data as {
          message?: string;
          errors?: Array<{ field: string; message: string }>;
        };
        if (Array.isArray(data.errors) && data.errors.length > 0) {
          const apiPeriodErrors: PeriodFieldError[] = periods.map(() => ({}));
          let mapped = false;
          for (const item of data.errors) {
            const m = item.field.match(/^periods\.(\d+)\.(start|end|label)$/);
            if (m) {
              const idx = Number(m[1]);
              const key = m[2] as 'start' | 'end' | 'label';
              if (apiPeriodErrors[idx]) {
                apiPeriodErrors[idx][key] = item.message;
                mapped = true;
              }
            }
          }
          if (mapped) {
            setPeriodErrors(apiPeriodErrors);
            setError(t('errors.validation'));
            return;
          }
        }
        setError(data.message || t('errors.validation'));
        return;
      }
      setError(getErrorMessage(err));
    } finally {
      setIsLoading(false);
    }
  };

  if (!isPremiumOrAbove) {
    return (
      <div className="relative bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="opacity-40 pointer-events-none select-none p-6 space-y-4" aria-hidden="true">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 blur-sm">
            <div className="h-32 bg-gradient-to-br from-emerald-100 to-emerald-50 rounded-xl" />
            <div className="h-32 bg-gradient-to-br from-rose-100 to-rose-50 rounded-xl" />
            <div className="h-32 bg-gradient-to-br from-blue-100 to-blue-50 rounded-xl" />
          </div>
          <div className="h-64 bg-gray-100 rounded-xl blur-sm" />
        </div>

        <div className="absolute inset-0 flex items-center justify-center bg-gradient-to-b from-white/70 to-white/95">
          <div className="flex flex-col items-center gap-4 p-8 text-center max-w-lg">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-primary-100 to-purple-100 flex items-center justify-center">
              <Sparkles className="w-8 h-8 text-primary-600" />
            </div>
            <h3 className="text-xl font-semibold text-gray-900">{t('analytics.lockedTitle')}</h3>
            <p className="text-sm text-gray-600">{t('analytics.lockedMessage')}</p>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 w-full mt-2">
              {features.map(({ icon: Icon, key }) => (
                <div key={key} className="flex flex-col items-center gap-2 p-3 rounded-lg bg-gray-50 border border-gray-100">
                  <Icon className="w-5 h-5 text-primary-600" />
                  <div className="text-xs font-medium text-gray-700 text-center">
                    {t(`analytics.features.${key}.title`)}
                  </div>
                  <div className="text-[11px] text-gray-500 text-center">
                    {t(`analytics.features.${key}.desc`)}
                  </div>
                </div>
              ))}
            </div>

            <button
              type="button"
              onClick={() => navigate('/dashboard/abonnement')}
              className="flex items-center gap-2 px-6 py-2.5 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 transition-colors mt-2"
            >
              <Crown className="w-4 h-4" />
              {t('analytics.upgrade')}
            </button>
          </div>
        </div>
      </div>
    );
  }

  const baselineLabel = result?.periods[0].label ?? '';
  const comparisonLabel = result?.periods[result.periods.length - 1].label ?? '';

  return (
    <div className="space-y-4">
      <div className="bg-white rounded-xl border border-gray-200">
        <button
          type="button"
          onClick={() => setFiltersOpen((v) => !v)}
          className="w-full flex items-center justify-between gap-3 px-4 py-3 text-left"
          aria-expanded={filtersOpen}
        >
          <div className="flex items-center gap-2">
            <SlidersHorizontal className="w-4 h-4 text-gray-500" />
            <span className="text-sm font-medium text-gray-700">
              {t('analytics.searchFiltersTitle')}
            </span>
            {activeFiltersCount > 0 && (
              <span className="inline-flex items-center justify-center min-w-[1.25rem] h-5 px-1.5 rounded-full bg-primary-100 text-primary-700 text-[11px] font-semibold">
                {activeFiltersCount}
              </span>
            )}
          </div>
          <ChevronDown
            className={clsx(
              'w-4 h-4 text-gray-400 transition-transform',
              filtersOpen && 'rotate-180',
            )}
          />
        </button>
        {filtersOpen && (
          <div className="px-4 pb-4 border-t border-gray-100">
            <div className="pt-3">
              <SearchFilters
                filters={filters}
                onFiltersChange={onFiltersChange}
                onSubmit={() => { /* géré par le bouton Lancer l'analyse */ }}
                onReset={() => { /* géré par le bouton Lancer l'analyse */ }}
                personalAccounts={personalAccounts}
                sharedAccounts={sharedAccounts}
                expenseCategories={expenseCategories}
                incomeCategories={incomeCategories}
                partner1Name={partner1Name}
                partner2Name={partner2Name}
                isLoading={isLoading}
                errors={fieldErrors}
                hideActions
              />
            </div>
          </div>
        )}
      </div>

      <PeriodComparisonPicker
        periods={periods}
        onChange={handlePeriodsChange}
        onApplyPreset={handleApplyPreset}
        errors={periodErrors}
      />

      <div className="flex justify-end">
        <button
          type="button"
          onClick={handleSubmit}
          disabled={isLoading}
          className="flex items-center gap-2 px-6 py-2 rounded-lg bg-primary-600 text-white text-sm font-medium hover:bg-primary-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          <SearchIcon className="w-4 h-4" />
          {isLoading ? t('analytics.loading') : t('analytics.run')}
        </button>
      </div>

      {error && (
        <div className="flex items-start gap-2 bg-rose-50 border border-rose-200 rounded-lg p-4 text-sm text-rose-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <div>{error}</div>
        </div>
      )}

      {result && (
        <>
          <ComparisonTable periods={result.periods} />
          <ComparisonBarChart periods={result.periods} />
          <TopVariationsCard
            topIncreases={result.variations.topIncreases}
            topDecreases={result.variations.topDecreases}
            baselineLabel={baselineLabel}
            comparisonLabel={comparisonLabel}
          />
        </>
      )}

      {!result && !isLoading && (
        <div className="bg-white rounded-xl border border-gray-200 p-8 text-center text-gray-500 text-sm">
          {t('analytics.initialHint')}
        </div>
      )}
    </div>
  );
}
